import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import Icon from '@mui/material/Icon'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom-v5-compat'
import { z } from 'zod'

import { Form } from '@/app/component/form'
import SubmitButton from '@/app/component/form/submit-button'
import { useVerifyOTP } from '@/app/module/auth/hooks/use-verify-otp'
import { selectOTPToken } from '@/app/module/auth/store/session.slice'

type OTPFormValues = {
  otp: string
}

const VerifyOTPForm: React.FC = () => {
  const [verifyOTP, { isLoading }] = useVerifyOTP()

  const token = useSelector(selectOTPToken)

  const handleSubmit = (data: OTPFormValues) => verifyOTP({ passcode: data.otp }, token)

  return (
    <StyledCard>
      <CardContent>
        <IconContainer>
          <StyledIcon>account_circle</StyledIcon>
        </IconContainer>
        <Typography color="textSecondary" variant="caption" sx={{ marginBottom: 2, display: 'block' }}>
          Please enter your two-factor token
        </Typography>

        <Form
          defaultValues={defaultValues}
          fields={fields}
          schema={schema}
          shouldReset={false}
          onSubmit={handleSubmit}
          renderActions={({ isDirty }) => (
            <CardActions
              sx={{
                alignItem: 'center',
                justifyContent: 'space-between',
                paddingBottom: '1rem',
                paddingLeft: 0,
                paddingRight: 0,
              }}
            >
              <Link to="/recovery-code">
                <Button size="small" color="primary">
                  Verify using a recovery code instead
                </Button>
              </Link>
              <SubmitButton isDirty={isDirty} isSubmitting={isLoading}>
                Verify
              </SubmitButton>
            </CardActions>
          )}
        />

        <Typography color="textSecondary" variant="caption" sx={{ marginTop: 2, display: 'block' }}>
          You have enabled two-factor authentication for this account. Please open your authenticator app (Google
          Authenticator, Authy, etc) to retrieve a code.
        </Typography>
      </CardContent>
    </StyledCard>
  )
}

const schema = z.object({
  otp: z.string({ message: 'OTP code is required' }).min(1, 'OTP code is required'),
})

const defaultValues = {
  otp: '',
}

const fields = {
  otp: {
    fieldProps: {
      label: 'Code',
      name: 'otp',
      type: 'text',
      required: true,
      autoFocus: true,
    },
    type: 'text',
  },
} as const

const StyledCard = styled(Card)({
  maxWidth: '400px',
  margin: 'auto',
  marginTop: 'calc(60px + 5%)',
  marginBottom: '3rem',
  overflow: 'visible',
})

const IconContainer = styled(CardContent)({
  textAlign: 'center',
})

const StyledIcon = styled(Icon)(({ theme }) => ({
  fontSize: '4rem !important',
  color: theme.palette.primary.main,
}))

export default VerifyOTPForm
